/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * Tracks user interactions and conversions throughout the site
 */

import { isGaBlocked } from './isLikelyBot';
import { captureException } from './sentry';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    __gaBlocked?: boolean;
  }
}

/**
 * Track custom events in Google Analytics 4
 * Automatically checks IP exclusion before tracking
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    event_category?: string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  }
) => {
  // Check if tracking is disabled (bot gate, IP excluded or manual disable)
  if (typeof window === 'undefined' || isGaBlocked() || !window.gtag) {
    return;
  }

  // If gtag is a no-op function, tracking is disabled
  if (window.dataLayerSkipIPCheck !== true) {
    // IP check will be done by googleAnalytics.ts
    // If IP is excluded, gtag will be set to no-op
  }

  window.gtag('event', eventName, {
    ...eventParams,
    send_to: 'G-BWTVEGHZGY'
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (formName: string, formLocation: string) => {
  trackEvent('form_submit', {
    event_category: 'Contact',
    event_label: `${formName} - ${formLocation}`,
    value: 1
  });
};

/**
 * Track CTA clicks
 */
export const trackCTAClick = (ctaText: string, ctaLocation: string, ctaType: string = 'button') => {
  trackEvent('cta_click', {
    event_category: 'CTA',
    event_label: `${ctaText} - ${ctaLocation}`,
    cta_type: ctaType,
    value: 1
  });
};

/**
 * Track page views (for SPA navigation)
 */
export const trackPageView = (pagePath: string, pageTitle: string) => {
  if (typeof window !== 'undefined' && !isGaBlocked() && window.gtag) {
    window.gtag('config', 'G-BWTVEGHZGY', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

/**
 * Track service interest
 */
export const trackServiceInterest = (serviceName: string, serviceType: string) => {
  trackEvent('service_interest', {
    event_category: 'Service',
    event_label: serviceName,
    service_type: serviceType,
    value: 1
  });
};

/**
 * Track article reads
 */
export const trackArticleRead = (articleTitle: string, readTime: number) => {
  trackEvent('article_read', {
    event_category: 'Content',
    event_label: articleTitle,
    read_time: readTime,
    value: 1
  });
};

/**
 * Track video plays
 */
export const trackVideoPlay = (videoTitle: string, videoLocation: string) => {
  trackEvent('video_play', {
    event_category: 'Video',
    event_label: `${videoTitle} - ${videoLocation}`,
    value: 1
  });
};

/**
 * Track downloads
 */
export const trackDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', {
    event_category: 'Download',
    event_label: fileName,
    file_type: fileType,
    value: 1
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLink = (linkUrl: string, linkText: string) => {
  trackEvent('external_link_click', {
    event_category: 'Outbound',
    event_label: linkText,
    link_url: linkUrl,
    value: 1
  });
};

/**
 * Track phone number clicks
 */
export const trackPhoneClick = (phoneNumber: string, location: string) => {
  trackEvent('phone_click', {
    event_category: 'Contact',
    event_label: `${phoneNumber} - ${location}`,
    value: 1
  });
};

/**
 * Track email clicks
 */
export const trackEmailClick = (emailAddress: string, location: string) => {
  trackEvent('email_click', {
    event_category: 'Contact',
    event_label: `${emailAddress} - ${location}`,
    value: 1
  });
};

/**
 * Track WhatsApp clicks
 */
export const trackWhatsAppClick = (location: string) => {
  trackEvent('whatsapp_click', {
    event_category: 'Contact',
    event_label: location,
    value: 1
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (depth: number, pagePath: string) => {
  trackEvent('scroll_depth', {
    event_category: 'Engagement',
    event_label: `${depth}% - ${pagePath}`,
    scroll_depth: depth,
    value: 1
  });
};

/**
 * Track time on page
 */
export const trackTimeOnPage = (timeInSeconds: number, pagePath: string) => {
  if (timeInSeconds >= 30) { // Only track if user spent at least 30 seconds
    trackEvent('time_on_page', {
      event_category: 'Engagement',
      event_label: pagePath,
      time_on_page: timeInSeconds,
      value: 1
    });
  }
};

/**
 * Track conversion (for goals)
 */
export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('conversion', {
    event_category: 'Conversion',
    event_label: conversionType,
    value: value || 1
  });
};

/**
 * Track errors and exceptions
 */
export const trackError = (
  error: Error,
  errorInfo?: {
    componentStack?: string;
    errorBoundary?: boolean;
    [key: string]: any;
  }
) => {
  // Track in GA4
  const { errorBoundary, componentStack, ...restErrorInfo } = errorInfo || {};
  trackEvent('exception', {
    event_category: 'Error',
    event_label: error.message,
    error_message: error.message,
    error_name: error.name,
    error_stack: error.stack?.substring(0, 500), // Limit stack trace length
    error_boundary: errorBoundary || false,
    component_stack: componentStack?.substring(0, 500),
    fatal: false, // Set to true for critical errors
    ...restErrorInfo
  });

  // Send to Sentry
  captureException(error, {
    componentStack: componentStack,
    errorBoundary: errorBoundary || false,
    ...restErrorInfo
  });

  // Also log to console in development
  if (import.meta.env.DEV) {
    console.error('Error tracked:', error, errorInfo);
  }
};

/**
 * Track section views (e.g., FAQ, Services, Testimonials)
 */
export const trackSectionView = (sectionName: string, pagePath: string) => {
  trackEvent('section_view', {
    event_category: 'Engagement',
    event_label: `${sectionName} - ${pagePath}`,
    section_name: sectionName,
    page_path: pagePath,
    value: 1
  });
};

/**
 * Track calculator usage
 */
export const trackCalculatorUsage = (
  calculatorType: string,
  action: 'open' | 'calculate' | 'reset',
  result?: any
) => {
  trackEvent('calculator_usage', {
    event_category: 'Interactive',
    event_label: `${calculatorType} - ${action}`,
    calculator_type: calculatorType,
    action: action,
    ...(result && { result: JSON.stringify(result).substring(0, 200) }),
    value: 1
  });
};

/**
 * Track form abandonment (when user starts but doesn't submit)
 */
export const trackFormAbandonment = (
  formName: string,
  formLocation: string,
  fieldsCompleted: number,
  totalFields: number
) => {
  trackEvent('form_abandonment', {
    event_category: 'Conversion',
    event_label: `${formName} - ${formLocation}`,
    form_name: formName,
    form_location: formLocation,
    completion_percentage: Math.round((fieldsCompleted / totalFields) * 100),
    fields_completed: fieldsCompleted,
    total_fields: totalFields,
    value: 1
  });
};

/**
 * Track internal link clicks (for understanding navigation patterns)
 */
export const trackInternalLink = (
  linkText: string,
  fromPage: string,
  toPage: string
) => {
  trackEvent('internal_link_click', {
    event_category: 'Navigation',
    event_label: `${linkText} - ${fromPage} → ${toPage}`,
    link_text: linkText,
    from_page: fromPage,
    to_page: toPage,
    value: 1
  });
};

/**
 * Track FAQ interactions
 */
export const trackFAQInteraction = (
  question: string,
  action: 'expand' | 'collapse',
  pagePath: string
) => {
  trackEvent('faq_interaction', {
    event_category: 'Engagement',
    event_label: `${action} - ${question.substring(0, 50)}`,
    faq_question: question.substring(0, 100),
    action: action,
    page_path: pagePath,
    value: 1
  });
};

/**
 * Track video interactions
 */
export const trackVideoInteraction = (
  videoTitle: string,
  action: 'play' | 'pause' | 'complete' | 'seek',
  videoLocation: string,
  progress?: number
) => {
  trackEvent('video_interaction', {
    event_category: 'Video',
    event_label: `${action} - ${videoTitle}`,
    video_title: videoTitle,
    action: action,
    video_location: videoLocation,
    ...(progress !== undefined && { progress_percentage: progress }),
    value: 1
  });
};

/**
 * Track engagement score (composite metric)
 */
export const trackEngagementScore = (
  pagePath: string,
  metrics: {
    scrollDepth: number;
    timeOnPage: number;
    interactions: number;
    sectionsViewed: number;
  }
) => {
  // Calculate engagement score (0-100)
  const scrollScore = metrics.scrollDepth;
  const timeScore = Math.min(100, (metrics.timeOnPage / 60) * 100); // 60s = 100%
  const interactionScore = Math.min(100, metrics.interactions * 10);
  const sectionScore = Math.min(100, metrics.sectionsViewed * 20);
  
  const engagementScore = Math.round(
    (scrollScore * 0.3 + timeScore * 0.3 + interactionScore * 0.2 + sectionScore * 0.2)
  );

  if (engagementScore >= 50) { // Only track meaningful engagement
    trackEvent('engagement_score', {
      event_category: 'Engagement',
      event_label: `${engagementScore}% - ${pagePath}`,
      page_path: pagePath,
      engagement_score: engagementScore,
      scroll_depth: metrics.scrollDepth,
      time_on_page: metrics.timeOnPage,
      interactions: metrics.interactions,
      sections_viewed: metrics.sectionsViewed,
      value: engagementScore
    });
  }
};

/**
 * Track funnel progression
 */
export const trackFunnelStep = (
  funnelName: string,
  step: number,
  stepName: string,
  userId?: string
) => {
  trackEvent('funnel_step', {
    event_category: 'Conversion',
    event_label: `${funnelName} - ${stepName}`,
    funnel_name: funnelName,
    step_number: step,
    step_name: stepName,
    ...(userId && { user_id: userId }),
    value: step
  });
};

/**
 * Track page visibility changes (when user switches tabs)
 */
export const trackVisibilityChange = (
  isVisible: boolean,
  pagePath: string,
  timeOnPage: number
) => {
  if (!isVisible && timeOnPage >= 10) { // Only track if user was on page for at least 10s
    trackEvent('page_visibility_change', {
      event_category: 'Engagement',
      event_label: `hidden - ${pagePath}`,
      page_path: pagePath,
      time_on_page: timeOnPage,
      value: 1
    });
  }
};

/**
 * Core Web Vitals Tracking
 * Tracks LCP, FID/INP, CLS for SEO and performance monitoring
 */

// Store for CLS calculation
let clsValue = 0;
let clsReported = false;

// Store for INP calculation
let inpValue = 0;
let inpReported = false;

/**
 * Initialize Core Web Vitals tracking
 * Call this once when app loads
 */
export const initCoreWebVitals = () => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  try {
    // LCP - Largest Contentful Paint
    // Good: < 2.5s, Needs Improvement: 2.5s - 4s, Poor: > 4s
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      
      if (lastEntry) {
        const lcpValue = Math.round(lastEntry.startTime);
        trackEvent('web_vitals', {
          event_category: 'Core Web Vitals',
          event_label: 'LCP',
          metric_name: 'LCP',
          metric_value: lcpValue,
          metric_rating: lcpValue < 2500 ? 'good' : lcpValue < 4000 ? 'needs-improvement' : 'poor',
          value: lcpValue
        });
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

    // FID - First Input Delay (deprecated, but still useful)
    // Good: < 100ms, Needs Improvement: 100ms - 300ms, Poor: > 300ms
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: PerformanceEntry & { processingStart?: number; startTime: number }) => {
        if (entry.processingStart !== undefined) {
          const fidValue = Math.round(entry.processingStart - entry.startTime);
          trackEvent('web_vitals', {
            event_category: 'Core Web Vitals',
            event_label: 'FID',
            metric_name: 'FID',
            metric_value: fidValue,
            metric_rating: fidValue < 100 ? 'good' : fidValue < 300 ? 'needs-improvement' : 'poor',
            value: fidValue
          });
        }
      });
    });
    fidObserver.observe({ type: 'first-input', buffered: true });

    // CLS - Cumulative Layout Shift
    // Good: < 0.1, Needs Improvement: 0.1 - 0.25, Poor: > 0.25
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as (PerformanceEntry & { hadRecentInput?: boolean; value: number })[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report CLS when page is hidden (most accurate)
    const reportCLS = () => {
      if (!clsReported) {
        clsReported = true;
        const clsRounded = Math.round(clsValue * 1000) / 1000;
        trackEvent('web_vitals', {
          event_category: 'Core Web Vitals',
          event_label: 'CLS',
          metric_name: 'CLS',
          metric_value: clsRounded,
          metric_rating: clsRounded < 0.1 ? 'good' : clsRounded < 0.25 ? 'needs-improvement' : 'poor',
          value: Math.round(clsRounded * 1000) // Convert to integer for GA
        });
      }
    };

    // INP - Interaction to Next Paint (replaces FID)
    // Good: < 200ms, Needs Improvement: 200ms - 500ms, Poor: > 500ms
    if (PerformanceObserver.supportedEntryTypes?.includes('event')) {
      const inpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as (PerformanceEntry & { duration: number; interactionId?: number })[]) {
          if (entry.interactionId && entry.duration > inpValue) {
            inpValue = entry.duration;
          }
        }
      });
      inpObserver.observe({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit);

      // Report INP when page is hidden
      const reportINP = () => {
        if (!inpReported && inpValue > 0) {
          inpReported = true;
          trackEvent('web_vitals', {
            event_category: 'Core Web Vitals',
            event_label: 'INP',
            metric_name: 'INP',
            metric_value: Math.round(inpValue),
            metric_rating: inpValue < 200 ? 'good' : inpValue < 500 ? 'needs-improvement' : 'poor',
            value: Math.round(inpValue)
          });
        }
      };

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          reportCLS();
          reportINP();
        }
      });
    } else {
      // Fallback: Report CLS on visibility change only
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          reportCLS();
        }
      });
    }

    // TTFB - Time to First Byte (bonus metric)
    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navigationEntries.length > 0) {
      const navEntry = navigationEntries[0];
      const ttfb = Math.round(navEntry.responseStart - navEntry.requestStart);
      
      if (ttfb > 0) {
        trackEvent('web_vitals', {
          event_category: 'Core Web Vitals',
          event_label: 'TTFB',
          metric_name: 'TTFB',
          metric_value: ttfb,
          metric_rating: ttfb < 800 ? 'good' : ttfb < 1800 ? 'needs-improvement' : 'poor',
          value: ttfb
        });
      }
    }

    // FCP - First Contentful Paint (bonus metric)
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcpEntry = entries.find(entry => entry.name === 'first-contentful-paint');
      
      if (fcpEntry) {
        const fcpValue = Math.round(fcpEntry.startTime);
        trackEvent('web_vitals', {
          event_category: 'Core Web Vitals',
          event_label: 'FCP',
          metric_name: 'FCP',
          metric_value: fcpValue,
          metric_rating: fcpValue < 1800 ? 'good' : fcpValue < 3000 ? 'needs-improvement' : 'poor',
          value: fcpValue
        });
        fcpObserver.disconnect();
      }
    });
    fcpObserver.observe({ type: 'paint', buffered: true });

  } catch (error) {
    // Silently fail - Core Web Vitals are not critical
    if (import.meta.env.DEV) {
      console.warn('Core Web Vitals tracking not supported:', error);
    }
  }
};

/**
 * Track user properties for audience segmentation
 */
export const setUserProperties = (properties: {
  language?: string;
  source?: string;
  userType?: 'investor' | 'expat' | 'nomad' | 'retiree' | 'unknown';
  visitCount?: number;
}) => {
  if (typeof window !== 'undefined' && !isGaBlocked() && window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};

